import { useQuery } from "@apollo/client";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Title, useTranslate } from "react-admin";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";

import { CrmCard } from "../components/crm/CrmCard";
import { CrmDataTable, type CrmSort } from "../components/crm/CrmDataTable";
import { CrmPageHeader } from "../components/crm/CrmPageHeader";
import { ListPagination } from "../components/ListPagination";
import { RequirePermission } from "../components/RequirePermission";
import { ResourceName } from "../constants/resources";
import {
  type CrmObject,
  CrmSortDirection,
  CustomerServiceCrmListDocument,
  type CustomerServiceCrmListQuery,
  type CustomerServiceCrmListQueryVariables,
} from "../generated/graphql";
import { useCrmLabels } from "../hooks/useCrmLabels";
import {
  CRM_COLORS,
  crmFilterParams,
  type CrmListFilter,
  crmListNeedsMarketing,
  crmObjectFromSlug,
  crmRecordPath,
  formatCrmCount,
  formatCrmMonthKey,
  readCrmFilters,
  readCrmRelated,
} from "../lib/crm";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

const ListViewPicker = ({
  object,
  listView,
  listViews,
  onChange,
}: {
  object: CrmObject;
  listView: string;
  listViews: string[];
  onChange: (listView: string) => void;
}) => {
  const translate = useTranslate();
  const labels = useCrmLabels();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <Button
        onClick={event => setAnchor(event.currentTarget)}
        endIcon={<ArrowDropDownIcon />}
        aria-haspopup="listbox"
        aria-label={translate("crm.list.listViewLabel")}
        sx={{
          p: 0,
          minWidth: 0,
          fontSize: "20px",
          fontWeight: 700,
          lineHeight: 1.25,
          color: CRM_COLORS.text,
          textTransform: "none",
          "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
        }}
      >
        {labels.listView(object, listView)}
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {listViews.map(key => (
          <MenuItem
            key={key}
            selected={key === listView}
            onClick={() => {
              setAnchor(null);
              onChange(key);
            }}
            sx={{ fontSize: "13px" }}
          >
            {labels.listView(object, key)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const FilterChips = ({
  object,
  filters,
  onRemove,
}: {
  object: CrmObject;
  filters: CrmListFilter[];
  onRemove: (filter: CrmListFilter) => void;
}) => {
  const translate = useTranslate();
  const labels = useCrmLabels();
  const describe = (filter: CrmListFilter): string => {
    if (!filter.fromMonth) return filter.value ?? translate("crm.list.noValue");
    const from = formatCrmMonthKey(filter.fromMonth);
    return filter.toMonth && filter.toMonth !== filter.fromMonth
      ? `${from} – ${formatCrmMonthKey(filter.toMonth)}`
      : from;
  };
  return (
    <Box
      aria-label={translate("crm.list.filters")}
      sx={{ display: "flex", flexWrap: "wrap", gap: "8px", px: "16px", pb: "12px" }}
    >
      {filters.map(filter => (
        <Chip
          key={crmFilterParams([filter])[0][0]}
          size="small"
          label={`${labels.field(object, filter.key)}: ${describe(filter)}`}
          onDelete={() => onRemove(filter)}
          sx={{ backgroundColor: CRM_COLORS.cardBackground, border: `1px solid ${CRM_COLORS.cardBorder}` }}
        />
      ))}
    </Box>
  );
};

const CrmList = ({ object }: { object: CrmObject }) => {
  const translate = useTranslate();
  const labels = useCrmLabels();
  const [params, setParams] = useSearchParams();
  const related = readCrmRelated(params);
  const search = params.get("q") ?? "";
  const filters = readCrmFilters(params);
  const [searchInput, setSearchInput] = useState(search);
  const sortKey = params.get("sort");
  const sortDirection = params.get("dir") === "desc" ? CrmSortDirection.Desc : CrmSortDirection.Asc;

  const update = (changes: Record<string, string | null>) =>
    setParams(
      current => {
        const next = new URLSearchParams(current);
        Object.entries(changes).forEach(([key, value]) => (value === null ? next.delete(key) : next.set(key, value)));
        return next;
      },
      { replace: true }
    );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (searchInput.trim() !== search) update({ q: searchInput.trim() || null });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // Only the typed text starts a new search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Follows a search changed elsewhere, such as the menu or the back button.
  useEffect(() => {
    setSearchInput(current => (current.trim() === search ? current : search));
  }, [search]);

  const input: CustomerServiceCrmListQueryVariables["input"] = {
    object,
    listView: params.get("view"),
    search: search || null,
    sortKey,
    sortDirection: sortKey ? sortDirection : null,
    limit: PAGE_SIZE,
    related,
    filters: filters.length > 0 ? filters : null,
  };
  const contextKey = JSON.stringify(input);
  const [pagination, setPagination] = useState({ contextKey, page: 1 });
  const page = pagination.contextKey === contextKey ? pagination.page : 1;
  if (pagination.contextKey !== contextKey) setPagination({ contextKey, page: 1 });
  const variables: CustomerServiceCrmListQueryVariables = {
    input: { ...input, offset: (page - 1) * PAGE_SIZE },
  };
  const { data, loading, error, refetch } = useQuery<CustomerServiceCrmListQuery, CustomerServiceCrmListQueryVariables>(
    CustomerServiceCrmListDocument,
    { variables, fetchPolicy: "cache-and-network", notifyOnNetworkStatusChange: true }
  );
  const list = data?.customerServiceCrmList;
  const sameObject = list?.object === object;
  const shown = sameObject ? list : undefined;
  if (shown && !loading && !error) {
    const lastPage = Math.max(1, Math.ceil(shown.totalCount / PAGE_SIZE));
    if (page > lastPage) setPagination({ contextKey, page: lastPage });
  }

  const sort: CrmSort | undefined = sortKey ? { key: sortKey, direction: sortDirection } : undefined;
  const onSort = (key: string) =>
    update({
      sort: key,
      dir: sort?.key === key && sort.direction === CrmSortDirection.Asc ? "desc" : "asc",
    });

  const objectLabel = labels.object(object, true);
  const sortColumn = sort?.key ?? null;

  return (
    <Box>
      <Title title={related ? labels.related(related.key) : objectLabel} />
      <CrmPageHeader
        object={object}
        overline={related ? labels.object(related.object) : objectLabel}
        title={
          related ? (
            labels.related(related.key)
          ) : shown ? (
            <ListViewPicker
              object={object}
              listView={shown.listView}
              listViews={shown.listViews}
              // Each view may have its own columns, so its own default sort.
              onChange={listView => update({ view: listView, sort: null, dir: null })}
            />
          ) : (
            objectLabel
          )
        }
        meta={
          shown && (
            <>
              {translate("crm.list.count", { total: formatCrmCount(shown.totalCount) })}
              {sortColumn && ` • ${translate("crm.list.sortedBy", { column: labels.field(object, sortColumn) })}`}
            </>
          )
        }
        actions={
          <>
            {related && (
              <Button
                component={RouterLink}
                to={crmRecordPath(related.object, related.salesforceId)}
                startIcon={<ArrowBackOutlinedIcon />}
                variant="outlined"
                size="small"
                sx={{ textTransform: "none", backgroundColor: CRM_COLORS.cardBackground }}
              >
                {translate("crm.list.backToRecord")}
              </Button>
            )}
            <TextField
              size="small"
              value={searchInput}
              onChange={event => setSearchInput(event.target.value)}
              placeholder={translate("crm.list.searchPlaceholder")}
              inputProps={{ "aria-label": translate("crm.list.searchLabel", { list: objectLabel }) }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                sx: { backgroundColor: CRM_COLORS.cardBackground, fontSize: "13px", width: 260 },
              }}
            />
          </>
        }
      >
        {filters.length > 0 && (
          <FilterChips
            object={object}
            filters={filters}
            onRemove={filter => update(Object.fromEntries(crmFilterParams([filter]).map(([name]) => [name, null])))}
          />
        )}
      </CrmPageHeader>
      {error && (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => void refetch().catch(() => undefined)}
              disabled={loading}
            >
              {translate("ra.action.refresh")}
            </Button>
          }
        >
          {translate("crm.loadError")}
        </Alert>
      )}
      {loading && !shown && <CircularProgress aria-label={translate("crm.loading")} />}
      {shown && (
        <CrmCard flush>
          {shown.rows.length === 0 ? (
            <Typography sx={{ p: "16px", fontSize: "13px", color: CRM_COLORS.textWeak }}>
              {translate("crm.list.empty")}
            </Typography>
          ) : (
            <CrmDataTable
              object={object}
              columns={shown.columns}
              rows={shown.rows}
              sort={sort}
              onSort={onSort}
              maxHeight="calc(100vh - 300px)"
            />
          )}
          <ListPagination
            page={page}
            perPage={PAGE_SIZE}
            total={shown.totalCount}
            resource={ResourceName.Crm}
            onPageChange={next => setPagination({ contextKey, page: next })}
            isLoading={loading}
          />
        </CrmCard>
      )}
    </Box>
  );
};

const CrmListRoute = () => {
  const translate = useTranslate();
  const { objectSlug } = useParams();
  const [params] = useSearchParams();
  const object = crmObjectFromSlug(objectSlug);
  if (!object) return <Alert severity="warning">{translate("crm.record.notFound")}</Alert>;
  const list = <CrmList key={object} object={object} />;
  return crmListNeedsMarketing(object, readCrmRelated(params)) ? (
    <RequirePermission permission={ResourceName.Marketing}>{list}</RequirePermission>
  ) : (
    list
  );
};

export const CrmListPage = () => (
  <RequirePermission permission={ResourceName.Crm}>
    <CrmListRoute />
  </RequirePermission>
);
